<template>
  <vx-card class="p-pdf">
    <form-wizard
      color="rgba(var(--vs-primary), 1)"
      title=""
      :progress-bar="true"
      :subtitle="null"
      @on-complete="formSubmitted"
    >
      <tab-content title="Subir Documento" class="mb-5 p-pdf" :before-change="validateStep1">
        <div class="excel-import">
          <input type="file" ref="fileInput" class="hidden" accept=".pdf" @change="handleClick">
          <div
            @click="$refs.fileInput.click()"
            @drop="handleDrop"
            @dragover="handleDragover"
            @dragenter="handleDragover"
            class="px-8 py-6 cursor-pointer text-center border-2 border-dashed d-theme-border-grey-light d-theme-dark-bg text-xl">
            <feather-icon icon="UploadCloudIcon" svgClasses="h-16 w-16 stroke-current text-grey" class="block" />
            <div v-if="!pdf">
              <span>Arrastra o </span>
              <span class="font-medium text-primary" @click.stop="$refs.fileInput.click()">selecciona tu documento</span>
            </div>
            <span class="block" v-else><b>{{pdf.name}}</b></span>
          </div>
        </div>
      </tab-content>

      <tab-content title="Agregar Firmantes" class="mb-5" :before-change="validateStep2">
        <div class="vx-row">

          <div class="vx-col lg:w-1/2 w-full p-pdf mb-5">
            <ReactPdfViewer class="background-fb card-p" v-if="isShowingPdf" :pdf="showPdf" height="500px"></ReactPdfViewer>
          </div>

          <div class="vx-col lg:w-1/2 w-full">

            <div class="vx-row">
              <div class="vx-col w-full">
                <h3 class="mb-3">Añade firmantes</h3>
                <p>Ingresa el nombre y correo de las personas que firmarán el documento.</p>
              </div>

              <!-- Interruptor para activar firma ordenada -->
              <div class="vx-col w-full mb-4 mt-3">
                <div class="flex items-center">
                  <span class="mr-3">Activar firma ordenada</span>
                  <vs-switch v-model="sortableSignatures"/>
                </div>
                <p class="mt-2 text-sm text-muted" v-if="sortableSignatures">
                  Arrastra las tarjetas para establecer el orden de firma.
                </p>
              </div>

              <!-- Renderizar los SignerCard dentro de draggable -->
              <div class="vx-col w-full">
                <draggable v-model="signatures" :disabled="!sortableSignatures" handle=".drag-handle" ghost-class="dragging">
                  <transition-group>
                    <SignerCard
                      v-for="(signer, index) in signatures"
                      :key="signer.id"
                      :signer="signer"
                      :index="index"
                      :sortable="sortableSignatures"
                      @remove-signer="removeSigner"
                      @popUpLimitFunction="popUpLimitFunction"
                      @search-signers="searchSigners"
                      :email-suggestions="emailSuggestions"
                      :ref="'signerCard' + index"
                    />
                  </transition-group>
                </draggable>
              </div>

              <!-- Botón para añadir nuevo firmante -->
              <div class="vx-col w-full mb-2 mt-4">
                <vs-button class="mt-1" type="flat" @click="addNewSigner"><feather-icon icon="PlusIcon" svgClasses="w-4 h-4" /> Añadir otro firmante</vs-button>
              </div>

            </div>

          </div>
        </div>
      </tab-content>

      <tab-content title="Revisión y Confirmación" class="mb-2">
        <div class="vx-row">

          <div class="vx-col md:w-full">
            <!-- Nueva sección de Firmas admitidas -->
            <div class="signature-types-section mt-5 mb-5">
              <h3 class="mb-4 text-center">Firmas admitidas para este documento</h3>

              <div class="signature-types-grid">
                <!-- Firma con identidad -->
                <div class="signature-type-card" :class="{ 'active': signatureType.includes('VERIFICAMEX'), 'disabled': !canDisableSimpleAndFirelType }">
                  <div class="card-content" @click="toggleSignatureType('VERIFICAMEX')">
                    <div class="icon-wrapper">
                      <img src="@/assets/images/document/id.svg" alt="ID" class="signature-icon">
                    </div>
                    <h4>Firma con identidad</h4>
                    <p class="description">(requiere INE)</p>
                    <div @click.stop>
                      <vs-checkbox
                        v-model="signatureType"
                        vs-value="VERIFICAMEX"
                        :disabled="!canDisableSimpleAndFirelType"
                        @change="toggleSignatureType('VERIFICAMEX', true)"
                      />
                    </div>
                  </div>
                </div>

                <!-- Firma simple -->
                <div class="signature-type-card" :class="{ 'active': signatureType.includes('VERIFICAMEX_LOCAL'), 'disabled': !canDisableSimpleAndFirelType }">
                  <div class="card-content" @click="toggleSignatureType('VERIFICAMEX_LOCAL')">
                    <div class="icon-wrapper">
                      <img src="@/assets/images/document/sign.svg" alt="Sign" class="signature-icon">
                    </div>
                    <h4>Firma simple</h4>
                    <p class="description">(para extranjeros)</p>
                    <div @click.stop>
                      <vs-checkbox
                        v-model="signatureType"
                        vs-value="VERIFICAMEX_LOCAL"
                        :disabled="!canDisableSimpleAndFirelType"
                        @change="toggleSignatureType('VERIFICAMEX_LOCAL', true)"
                      />
                    </div>
                  </div>
                </div>

                <!-- Firma Avanzada SAT -->
                <div class="signature-type-card" :class="{ 'active': signatureType.includes('EFIRMA'), 'disabled': !canDisableEfirmaType }">
                  <div class="card-content" @click="toggleSignatureType('EFIRMA')">
                    <div class="icon-wrapper">
                      <img src="@/assets/images/document/sat.svg" alt="SAT" class="signature-icon">
                    </div>
                    <h4>Firma Avanzada</h4>
                    <p class="description">con e.firma (SAT)</p>
                    <div @click.stop>
                      <vs-checkbox
                        v-model="signatureType"
                        vs-value="EFIRMA"
                        :disabled="!canDisableEfirmaType"
                        @change="toggleSignatureType('EFIRMA', true)"
                      />
                    </div>
                  </div>
                </div>

                <!-- Firma Avanzada FIREL -->
                <div class="signature-type-card" :class="{ 'active': signatureType.includes('FIREL'), 'disabled': !canDisableSimpleAndFirelType }">
                  <div class="card-content" @click="toggleSignatureType('FIREL')">
                    <div class="icon-wrapper">
                      <img src="@/assets/images/document/firel.svg" alt="FIREL" class="signature-icon">
                    </div>
                    <h4>Firma Avanzada</h4>
                    <p class="description">con FIREL (PJF)</p>
                    <div @click.stop>
                      <vs-checkbox
                        v-model="signatureType"
                        vs-value="FIREL"
                        :disabled="!canDisableSimpleAndFirelType"
                        @change="toggleSignatureType('FIREL', true)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="vx-col md:w-1/2 w-full">
            <h3 class="mb-2">Importante:</h3>
            <p class="mb-3">Revisa el documento y los firmantes antes de enviar las invitaciones, ya que todos serán notificados. Una vez firmado el documento no podrá ser modificado.</p>

            <h3 class="mb-2">Recordatorios:</h3>
            <span>Se enviarán {{ tries }} recordatorios de firmado cada {{ remember_every }} dias, a las {{ remember_at }} hrs. <a style="cursor: pointer" @click="rememberPopupEnable = true">Modificar.</a></span>

            <h3 class="mt-5 mb-2">PDF Personalizado:</h3>
            <span>Personaliza tu PDF con el logo de tu marca o empresa. <a style="cursor: pointer" @click="customLogo = true">Personalizar.</a></span>

            <h3 class="mt-5 mb-2">Tipo de representación de firmado</h3>
            <span>Selecciona una de las dos tipos de representaciones que ponemos a tu disposición (PAdES por defecto). <a
              style="cursor: pointer" @click="signedMethodPopupEnable = true">Seleccionar</a></span> <br>
            <span>Tipo de firmado: <strong>{{ SIGN_STRATEGIES[signMode] }}</strong></span> <br>
            <span v-if="signMode === 'PDF_SIGNATURE'">Posición seleccionada: <strong>{{ SIGN_POSITION[signPosition] }}</strong></span>
          </div>

          <div class="vx-col md:w-1/2 w-full">
            <h3 class="mb-3">Detalles del documento</h3>
            <p class="text-bold">Nombre documento: {{ pdf ? pdf.name : "" }}</p>
            <vs-list>
              <vs-list-header title="Firmantes" color="primary"></vs-list-header>

              <vs-list-item class="list-item" v-for="listItem in signatures" :key="listItem.email" :title="listItem.name" :subtitle="listItem.email">
                <vs-avatar slot="avatar" :text="listItem.name.split(' ').slice(0,2).join(' ').trim()" />
              </vs-list-item>

            </vs-list>

            <vs-list >
              <vs-list-header title="Espectadores" color="primary"></vs-list-header>
              <div v-if="!spectators.length">
                <p>No hay espectadores agregados</p>
              </div>
              <vs-list-item v-if="spectators.length" class="list-item" v-for="(listItem) in spectators" :key="listItem.email" :title="listItem.name" :subtitle="listItem.email">
                <vs-avatar slot="avatar" :text="listItem.name.split(' ').slice(0,2).join(' ').trim()" />
                <vs-button
                  type="flat"
                  color="danger"
                  icon-pack="feather"
                  icon="icon-x"
                  @click="deleteSpectator(listItem.email)"
                ></vs-button>
              </vs-list-item>
            </vs-list>
            <!-- Botón para abrir el popup de añadir espectadores -->
            <vs-button type="border" class="mt-4" @click="spectatorPopupEnable = true">
              Añadir Espectadores
            </vs-button>
          </div>

        </div>
      </tab-content>


      <template slot="footer" slot-scope="props">
        <div class="wizard-footer-left">
          <wizard-button v-if="props.activeTabIndex > 0" @click.native="props.prevTab()" :style="props.fillButtonStyle">Atrás</wizard-button>
        </div>
        <div class="wizard-footer-right">
          <div>
            <wizard-button v-if="!props.isLastStep" @click.native="props.nextTab()" class="wizard-footer-right" :style="props.fillButtonStyle">Siguiente</wizard-button>
            <wizard-button v-else class="wizard-footer-right finish-button" @click.native="formSubmitted" :style="props.fillButtonStyle">{{props.isLastStep ? 'Finalizar' : 'Siguiente'}}</wizard-button>
          </div>
        </div>
      </template>

      <vs-popup class="signed_method"  title="Selecciona un Método de Firmado" :active.sync="signedMethodPopupEnable">
        <sign-selector :open-sign-position-popup="openSignPositionPopup" :submit-new-document="signSelectorAction"></sign-selector>
        <vs-popup title="Selecciona la posición de la firma" :active.sync="signPositionPopupEnable">
          <sign-position-selector :set-sign-position="setSignPosition" :submit-new-document="signSelectorAction"></sign-position-selector>
        </vs-popup>
      </vs-popup>

      <vs-popup title="Establecer Recordatorios" :active.sync="rememberPopupEnable">
        <vs-card>
          <div class="pt-3 px-3">
            <h3 class="mb-3">Recordatorios</h3>
            <p class="mb-5">Configura los recordatorios que recibirán tus invitados para firmar el documento</p>
            <h3 class="mb-3">Intentos</h3>
            <p class="mb-5">Establece el número total de recordatorios que deseas enviar.</p>
            <vs-input-number min="1" max="7" v-model="tries"/>
            <h3 class="mb-3">Intervalos</h3>
            <p class="mb-5">Seleciona un intervalo de días para hacerlo. (Cada día, cada 2 días, etc.).</p>
            <vs-input-number min="1" max="7" v-model="remember_every"/>
            <h3 class="mb-3">Hora de envío</h3>
            <p class="mb-5">Elije una hora para enviar los recordatorios</p>
            <flat-pickr :config="configDateTimePicker" v-model="remember_at" placeholder="Selecciona una hora"/>
          </div>
          <div slot="footer">
            <vs-row vs-justify="flex-end">
              <vs-button type="filled" color="primary" @click="rememberPopupEnable = false">Aceptar</vs-button>
            </vs-row>
          </div>
        </vs-card>
      </vs-popup>

      <vs-popup title="Logo Personalizado" :active.sync="customLogo">
        <vs-card>
          <div class="pt-3 px-3">
            <h3 class="mb-3">Hoja de firmas</h3>
            <p class="">Personaliza con el logo de tu empresa la hoja de firmas al final del documento.</p>
            <vs-row>
              <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
                <img src="@/assets/images/document/full-logo.svg" width="90" class="d-block m-auto" alt="">
                <vs-radio class="mt-3" v-model="logoMode" vs-name="logoMode" vs-value="FULL_BRANDING">Logos de Verificamex</vs-radio>
              </vs-col>

              <vs-col class="my-3" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
                <img src="@/assets/images/document/medium-logo.svg" width="90" class="d-block m-auto" alt="">
                <vs-radio class="mt-3" v-model="logoMode" vs-name="logoMode" vs-value="MEDIUM_BRANDING">Mi logo Personalizado</vs-radio>
              </vs-col>

              <vs-col class="mt-3 d-block m-auto" vs-type="grid" vs-justify="center" vs-align="center" vs-w="6">
                <img src="@/assets/images/document/upper-left.svg" width="90" class="d-block m-auto" alt="">
                <vs-radio class="mt-3" v-model="logoMode" vs-name="logoMode" vs-value="NO_BRANDING">Prefiero no usar logos</vs-radio>
              </vs-col>
            </vs-row>
          </div>
          <div slot="footer">
            <vs-row vs-justify="flex-end">
              <vs-button type="filled" color="primary" @click="customLogo = false">Aceptar</vs-button>
            </vs-row>
          </div>
        </vs-card>
      </vs-popup>

      <!-- Popup eliminado ya que ahora está integrado en la interfaz principal -->

      <vs-popup title="Activa Plan Profesional" :active.sync="upgradePro">
        <p>Esta función está disponible solo para el Plan Profesional, obtén todos los beneficios del Plan además con un 20% de descuento en todos los documentos extra que compres.</p>

        <planPro :upgradeProPopup="setUpgradePro"/>

      </vs-popup>

      <!-- Popup para añadir espectadores -->
      <vs-popup title="Añadir Espectador" :active.sync="spectatorPopupEnable">
        <div class="pt-3 px-3">
          <h3 class="mb-3">Nuevo Espectador</h3>
          <vs-input
            :danger="!$v.spectatorsForm.email_spectator.email && $v.spectatorsForm.email_spectator.$dirty"
            class="w-full mb-4"
            label-placeholder="Correo electrónico"
            v-model.trim="spectatorsForm.email_spectator"
            @blur="$v.spectatorsForm.email_spectator.$touch()"
          ></vs-input>
          <vs-input
            class="w-full mb-4"
            label-placeholder="Nombre completo"
            v-model.trim="spectatorsForm.name_spectator"
          ></vs-input>
          <div class="text-danger" v-if="!$v.spectatorsForm.email_spectator.email && $v.spectatorsForm.email_spectator.$dirty">
            El correo es inválido
          </div>
          <vs-button class="mt-4" @click="addSpectator">
            Añadir Espectador
          </vs-button>
        </div>
      </vs-popup>

      <!-- Popup de confirmación para firma simple -->
      <vs-popup title="Confirmación Tipo de Firmado Simple" :active.sync="lightConfirmationPopup">
        <div class="pt-3 px-3">
          <h3 class="mb-3">Configuración de Firmado Simple</h3>
          <p class="mb-4">Al seleccionar el tipo de firmado "Simple", <b>es necesario verificar los nombres de las personas invitadas</b> a firmar para mayor seguridad ya que serán estampados en el documento Final.</p>

          <vs-divider class="my-4"/>

          <h4 class="mb-3">Opciones adicionales de seguridad</h4>
          <vs-checkbox v-model="blockPhoneVerification" class="mb-3">
            Bloquear número de teléfono para verificación de SMS y Whatsapp
          </vs-checkbox>
          <p class="text-sm mb-4">Al activar esta opción, los firmantes deberán utilizar únicamente el número de teléfono registrado para validar el código que les llegará, aumentando la seguridad del proceso.</p>

          <div class="text-danger" v-if="!hasPhoneNumbers">
            <feather-icon icon="AlertTriangleIcon" class="mr-1" svgClasses="h-5 w-5" />
            <span>Advertencia: Algunos firmantes no tienen número de teléfono registrado. Es recomendable añadir sus números antes de continuar.</span>
          </div>
        </div>
        <div>
          <vs-divider class="my-4"/>

          <vs-row class="mt-4" vs-justify="flex-end">
            <vs-button type="border" class="mr-3" @click="cancelLightSignatureType">Cancelar</vs-button>
            <vs-button type="filled" color="primary" @click="confirmLightSignatureType">Confirmar</vs-button>
          </vs-row>
        </div>
      </vs-popup>

      <!-- Segundo Popup: Revisión de Firmantes -->
      <vs-popup title="Revisión de Firmantes" :active.sync="signersReviewPopup">
        <div class="pt-3 px-3">
          <h3 class="mb-3">Valida la información</h3>
          <p class="mb-5">Esta es la lista de las personas que firmarán el documento. Asegúrate de que sus nombres sean correctos.</p>

          <vs-list>
            <vs-list-item
              v-for="(signer, index) in signatures"
              :key="index"
              :title="signer.name || 'Sin nombre'"
              :subtitle="signer.email"
            >
              <vs-avatar slot="avatar" :text="(signer.name || 'F').split(' ').slice(0,2).join(' ').trim()" />
            </vs-list-item>
          </vs-list>

          <div class="mt-8">
            <vs-row vs-justify="flex-end">
              <vs-button type="border" class="mr-3" @click="cancelLightSignatureType">Atrás</vs-button>
              <vs-button type="filled" color="primary" @click="confirmFinalSignersReview">Confirmar y Continuar</vs-button>
            </vs-row>
          </div>
        </div>
      </vs-popup>

    </form-wizard>
  </vx-card>
</template>

<script>
import planPro from "@/views/pages/buy/plans/PlanPro.vue";
import router from "@/router";
import {FormWizard, TabContent, WizardButton} from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import draggable from 'vuedraggable';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {required, email, helpers, numeric, maxLength, minLength} from 'vuelidate/lib/validators';
import {mapState} from "vuex";

import {ReactInVue} from 'vuera';
import ReactPdfViewer from "../../../../components/viewer-pdf/ReactPdfViewer";
import SignSelector from "../components/SignSelector.vue";
import SignPositionSelector from "../components/SignPositionSelector.vue";
import {SIGN_POSITION, SIGN_STRATEGIES} from "../../../../config/app";
import SignerCard from "./SignerCard.vue";

var rfcRegexValidation = helpers.regex('rfcRegexValidation', /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/);
var curpRegexValidation = helpers.regex('curpRegexValidation', /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/);
var phoneRegexValidation = helpers.regex('phoneRegexValidation', /^[0-9]{10}$/);

export default {
  name: "createDocument",
  props: ['signMode', 'signPosition', 'company_id'],
  components: {
    SignPositionSelector, SignSelector,
    planPro,
    FormWizard,
    TabContent,
    WizardButton,
    draggable,
    flatPickr,
    'ReactPdfViewer': ReactInVue(ReactPdfViewer),
    SignerCard
  },
  data() {
    return {
      switch1:true,
      tabIndex: 1,
      focusValidation: false,
      upgradePro: false,
      rememberPopupEnable: false,
      customLogo: false,
      signatureTypes: false,
      isShowingPdf: true,
      pdf: null,
      base64Pdf: null,
      restrictSelector: "RFC",
      configDateTimePicker: {
        enableTime: true,
        enableSeconds: true,
        noCalendar: true
      },
      signaturesForm: {
        name: "",
        email: "",
        rfc: "",
      },
      spectatorsForm: {
        name_spectator: "",
        email_spectator: "",
      },
      signatures: [
        {
          id: (Math.random() + 1).toString(36).substring(7),
          name: '',
          email: '',
          restrictSign: false,
          restrictSelector: 'RFC',
          rfc: '',
        }
      ],
      spectators: [],
      sortableSignatures: false,
      restrictSign: false,
      remember_at: "12:30",
      tries: 3,
      remember_every: 3,
      logoMode: "FULL_BRANDING",
      signatureType: [
        'VERIFICAMEX', 'EFIRMA', 'FIREL', 'VERIFICAMEX_LOCAL'
      ],
      finished: false,
      signedMethodPopupEnable: false,
      signPositionPopupEnable: false,
      spectatorPopupEnable: false,
      emailSuggestions: [],
      lightConfirmationPopup: false,
      blockPhoneVerification: false,
      previousSignatureTypes: [],
      phonesUpdateTrigger: 0,
      signersReviewPopup: false,
    }
  },
  validations(){
    if (this.restrictSelector === 'RFC') {
      return {
        signaturesForm: {
          name: {
            required
          },
          phone: {
            numeric,
            maxLength: maxLength(10),
            minLength: minLength(10)
          },
          email: {
            required,
            email
          },
          rfc: {
            rfcRegexValidation
          },
        },
        spectatorsForm: {
          name_spectator: {
            required
          },
          email_spectator: {
            required,
            email
          }
        }
      }
    } else {
      return {
        signaturesForm: {
          name: {
            required
          },
          phone: {
            phoneRegexValidation
          },
          email: {
            required,
            email
          },
          rfc: {
            curpRegexValidation
          },
        },
        spectatorsForm: {
          name_spectator: {
          },
          email_spectator: {
            email
          }
        }
      }
    }
  },
  computed: {
    canDisableEfirmaType() {
      let signaturesWithNeededCertificate = this.signatures.filter((signature) => {
        return signature.restrictSelector === 'RFC' && signature.rfc.length;
      })
      return signaturesWithNeededCertificate.length === 0;
    },
    canDisableSimpleAndFirelType() {
      let signaturesWithNeededCertificate = this.signatures.filter((signature) => {
        return signature.restrictSelector === 'CURP' && signature.rfc.length;
      })
      return signaturesWithNeededCertificate.length === 0;
    },
    SIGN_POSITION() {
      return SIGN_POSITION
    },
    SIGN_STRATEGIES() {
      return SIGN_STRATEGIES
    },
    isAddMeAsSigner() {
      return this.tabIndex === 1;
    },

    isAddSigners() {
      return this.tabIndex === 2;
    },

    isAddSpectator() {
      return this.tabIndex === 3;
    },

    isSignersResume() {
      return this.isAddSpectator;
    },


    ...mapState({
      user: function (state) {
        return state.account.user;
      },
    }),
    currentUserInSignatures() {
      let isIn = this.signatures.filter(signature => signature.email === this.user.me.email).length;
      if (this.sortableSignatures) {
        let index = 0;
        let position = 0;
        this.signatures.forEach((signature) => {
          if (signature.email === this.user.me.email) {
            position = index;
          }
          index = index + 1;
        });
        return (position === 0) && isIn
      } else {
        return isIn;
      }
    },
    showPdf() {
      return this.base64Pdf;
    },
    hasPhoneNumbers() {
      // Esta línea crea una dependencia para que la computed se recalcule
      this.phonesUpdateTrigger;

      if (!this.signatures || this.signatures.length === 0) return true;
      return this.signatures.every(signer => signer.phone && signer.phone.trim() !== '');
    }
  },
  created() {
    if (!this.signMode && !this.signPosition && !this.company_id) {
      router.push("/");
    }
    this.$store.dispatch("account/getDefaultSettings").then(
      result => {
        let signers = result.data.find(
          item => item.attributes.settingKey === "default_signatures"
        );
        let viewers = result.data.find(
          item => item.attributes.settingKey === "default_spectators"
        )
        let sortableSignatures = result.data.find(
          item => item.attributes.settingKey === "sortable_signatures"
        )
        let allowSignatureTypes = result.data.find(
          item => item.attributes.settingKey === 'allow_type_signatures'
        )
        let documentMode = result.data.find(
          item => item.attributes.settingKey === 'document_mode'
        )
        let documentSignPosition = result.data.find(
          item => item.attributes.settingKey === "document_sign_position"
        )
        if (signers !== undefined) {
          let signersParsed = JSON.parse(signers.attributes.settingValue)
          if (signersParsed.length) {
            signersParsed = signersParsed.map(signer => {
              signer.id = (Math.random() + 1).toString(36).substring(7);
              if (signer.rfc) {
                signer.restrictSign = true;
                if (signer.rfc.length >= 14) {
                  signer.restrictSelector = 'CURP';
                } else {
                  signer.restrictSelector = 'RFC';
                }
                signer.rfc = signer.rfc.toUpperCase();
              } else {
                signer.restrictSign = false;
                signer.restrictSelector = 'RFC';
                signer.rfc = '';
              }
              return signer;
            })
            this.signatures = signersParsed;
          }
        }
        if (viewers !== undefined) {
          let spectators = JSON.parse(viewers.attributes.settingValue);
          if (spectators.length) {
            this.spectators = spectators;
          }
        }
        if (sortableSignatures !== undefined) {
          this.sortableSignatures = JSON.parse(sortableSignatures.attributes.settingValue)
        }
        if (allowSignatureTypes !== undefined) {
          let signatureTypes = JSON.parse(allowSignatureTypes.attributes.settingValue);
          this.signatureType = !signatureTypes.length ? ['VERIFICAMEX', 'EFIRMA', 'FIREL'] : signatureTypes;
        }
        if (documentMode !== undefined && documentMode !== null) {
          let mode = JSON.parse(documentMode.attributes.settingValue);
          this.signMode = mode == null ? 'PDF_SIGNATURE' : mode;
        }
        if (documentSignPosition !== undefined && documentSignPosition !== null) {
          let position = JSON.parse(documentSignPosition.attributes.settingValue);
          this.signPosition = position == null ? 'NONE' : position;
        }
      }
    )
  },
  methods: {
    // Nuevo método para alternar tipos de firma
    toggleSignatureType(type, fromCheckbox = false) {
      // Si el tipo está deshabilitado, no hacer nada
      if (type === 'VERIFICAMEX' && !this.canDisableSimpleAndFirelType) return;
      if (type === 'VERIFICAMEX_LOCAL' && !this.canDisableSimpleAndFirelType) return;
      if (type === 'EFIRMA' && !this.canDisableEfirmaType) return;
      if (type === 'FIREL' && !this.canDisableSimpleAndFirelType) return;

      // Guardar estado actual para el popup de confirmación
      this.previousSignatureTypes = [...this.signatureType];

      // Si no viene del checkbox, nosotros manejamos el toggle manual del array
      // Si viene del checkbox, el v-model ya hizo su trabajo
      if (!fromCheckbox) {
        const index = this.signatureType.indexOf(type);
        if (index > -1) {
          // Si ya está seleccionado, lo removemos
          this.signatureType.splice(index, 1);
        } else {
          // Si no está seleccionado, lo agregamos
          this.signatureType.push(type);
        }
      }

      // Lógica común (popups, etc)
      // Si se selecciona firma simple (ya sea manual o por checkbox), mostrar popup de confirmación
      if (type === 'VERIFICAMEX_LOCAL' && this.signatureType.includes('VERIFICAMEX_LOCAL')) {
        this.lightConfirmationPopup = true;
      }
    },



    addNewSigner() {
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: '',
        email: '',
        phone: '',
        restrictSign: false,
        restrictSelector: 'RFC',
        rfc: '',
      });
    },
    removeSigner(index) {
      this.signatures.splice(index, 1);
    },

    // Modificar validateStep2
    validateStep2() {
      this.$store.dispatch('alert/clear', {});
      return new Promise((resolve, reject) => {
        if (this.signatures.length > 0) {
          if (this.signatures.length === 1 && this.signatures[0].email === '' && this.signatures[0].name === '') {
            this.$store.dispatch('alert/error', 'Por favor, completa la información de todos los firmantes.');
            reject('Por favor, completa la información de todos los firmantes.');
          }
          resolve(true);
        } else {
          this.$store.dispatch('alert/error', 'Por favor, completa la información de todos los firmantes.');
          reject('Por favor, completa la información de todos los firmantes.');
        }
      });
    },

    popUpLimitFunction() {
      this.upgradePro = true;
    },
    openSignPositionPopup() {
      this.signPositionPopupEnable = true;
    },
    setSignPosition(position) {
      this.signPosition = position;
    },
    signSelectorAction(selector) {
      this.signMode = selector;
      this.signPositionPopupEnable = false;
      this.signedMethodPopupEnable = false;
    },
    changeRestrictSelector() {
      if (this.restrictSelector === 'RFC') {
        rfcRegexValidation = helpers.regex('rfcRegexValidation', /^([A-ZÑ&]{3,4})?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01]))?([A-Z\d]{2})([A\d])$/);
      } else {
        curpRegexValidation = helpers.regex('curpRegexValidation', /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/);
      }
    },
    toggleTab(index) {
      this.tabIndex = index;
    },

    btnNext() {
      this.tabIndex = this.tabIndex + 1 ;
    },
    btnBack() {
      this.tabIndex = this.tabIndex - 1 ;
    },
    addMeAsSignature() {
      this.tabIndex = 2 ;
      this.$store.dispatch("alert/clear", {});
      let findSignature = this.signatures.filter((signature) => signature.email === this.user.me.email)
      if (findSignature.length) {
        this.$store.dispatch("alert/error", "Ya estas en la lista");
        return;
      }
      if (this.signatures.length > 0 && this.signatures[0].email.length === 0) {
        this.signatures = [];
      }
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: this.user.me.name + " " + this.user.me.last_name,
        email: this.user.me.email,
        phone: this.user.me.phone,
        restrictSign: false,
        restrictSelector: 'RFC',
        rfc: '',
      });
      this.$store.dispatch("alert/success", "Te has añadido como Firmante");
    },
    deleteMeAsSignature() {
      this.$store.dispatch("alert/clear", {});
      this.signatures = this.signatures.filter((signature) => signature.email !== this.user.me.email)
      this.$store.dispatch("alert/success", "Configurado correctamente");
      this.btnNext();
    },
    deleteSignature(indexSignature) {
      this.$store.dispatch("alert/clear", {});
      this.signatures = this.signatures.filter((signature, index) => index !== indexSignature)
      this.$store.dispatch("alert/success", "Firmante eliminado correctamente");
    },
    deleteSpectator(email) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: '¿Eliminar espectador?',
        text: 'Esta acción no se puede deshacer.',
        acceptText: 'Eliminar',
        cancelText: 'Cancelar',
        accept: () => {
          this.spectators = this.spectators.filter(
            (spectator) => spectator.email !== email
          );
          this.$store.dispatch("alert/success", "Espectador eliminado correctamente.");
        },
      });
    },
    validationEval() {
      if (this.signaturesForm.name || this.signaturesForm.email) {
        this.focusValidation = true;
      } else {
        this.focusValidation = false;
      }
    },
    setUpgradePro(state) {
      this.upgradePro = state;
    },
    setName(value) {
      this.signaturesForm.name = value;
      this.$v.signaturesForm.name.$touch();
    },
    setEmail(value) {
      this.signaturesForm.email = value.replace(/\s+/g, '').trim();
      this.$v.signaturesForm.email.$touch();
    },
    setRfc(value) {
      console.log(this.$v);
      this.signaturesForm.rfc = value;
      this.$v.signaturesForm.rfc.$touch();
    },
    setNameSpectator(value) {
      this.spectatorsForm.name_spectator = value;
      this.$v.spectatorsForm.name_spectator.$touch();
    },
    setEmailSpectator(value) {
      this.spectatorsForm.email_spectator = value.replace(/\s+/g, '').trim();
      this.$v.spectatorsForm.email_spectator.$touch();
    },

    restrictSigner() {
      if (this.restrictSign){
        this.restrictSign = false;
      } else {
        this.restrictSign = true;
      }

    },
    redirectPlans(){
      this.upgradePro = false;
      this.rememberPopupEnable = false;
      this.customLogo = false;
      this.signatureTypes = false;
      setTimeout(() => {
        let routeData = this.$router.resolve({name: 'buy-plans'});
        window.open(routeData.href, '_blank');
        //router.push("/buy/plans");
      }, 100)
    },
    addSpectator() {
      this.$store.dispatch("alert/clear", {});
      this.$v.spectatorsForm.$touch();
      if (this.$v.spectatorsForm.$invalid) {
        this.$store.dispatch("alert/error", "Revise los datos ingresados.");
        return;
      }
      const newSpectator = {
        name: this.spectatorsForm.name_spectator.trim(),
        email: this.spectatorsForm.email_spectator.trim(),
      };
      const exists = this.spectators.some(
        (spectator) => spectator.email === newSpectator.email
      );
      if (exists) {
        this.$store.dispatch("alert/error", "El espectador ya está en la lista.");
        return;
      }
      this.spectators.push(newSpectator);
      this.spectatorsForm.name_spectator = "";
      this.spectatorsForm.email_spectator = "";
      this.$v.spectatorsForm.$reset();
      this.$store.dispatch("alert/success", "Espectador añadido correctamente.");
      this.spectatorPopupEnable = false;
    },
    searchSigners(query) {
      this.$store.dispatch("document/searchSigners", {query}).then(
        response => {
          console.log(response)
          this.emailSuggestions = response.data
        }
      ).catch(error => {
        console.log(error)
      })
    },
    addSignature() {
      this.$store.dispatch("alert/clear", {});
      this.$v.signaturesForm.$touch()
      if (this.$v.signaturesForm.$invalid) {
        this.$store.dispatch("alert/error", "Revise los datos ingresados.");
        return;
      }
      if (!this.signaturesForm.name || !this.signaturesForm.email) {
        this.$store.dispatch("alert/error", "Ingrese el nombre y correo electrónico del firmante para continuar");
        return;
      }

      let findSignature = this.signatures.filter((signature) => signature.email === this.signaturesForm.email)
      if (findSignature.length) {
        this.$store.dispatch("alert/error", "El firmante ya se encuentra en la lista");
        return;
      }
      this.signatures.push({
        id: (Math.random() + 1).toString(36).substring(7),
        name: this.signaturesForm.name.trim(),
        phone: this.signaturesForm.phone.trim(),
        email: this.signaturesForm.email.trim(),
        rfc: this.signaturesForm.rfc.trim()
      });
      this.signaturesForm.name = "";
      this.signaturesForm.phone = "";
      this.signaturesForm.email = "";
      this.signaturesForm.rfc = "";
      this.focusValidation = false;
      this.$store.dispatch("alert/success", "Firmante añadido correctamente");
    },
    formSubmitted(){
      this.$vs.loading({type: "point"})
      let foundLocal = this.signatureType.find((type) => type === 'VERIFICAMEX_LOCAL');
      let foundVerificamex = this.signatureType.find((type) => type === 'VERIFICAMEX');
      let identityProviders = [];
      if (foundLocal) {
        identityProviders.push("LOCAL");
      }
      if (foundVerificamex) {
        identityProviders.push("VERIFICAMEX");
      }

      // Creamos una copia para no afectar la reactividad en la UI durante el loading
      let typesToSend = [...this.signatureType];

      if (foundLocal && foundVerificamex) {
        typesToSend = typesToSend.filter((type) => type !== 'VERIFICAMEX_LOCAL');
      } else if (foundLocal) {
        typesToSend = typesToSend.filter((type) => type !== 'VERIFICAMEX_LOCAL');
        typesToSend.push('VERIFICAMEX');
      }

      this.$store.dispatch("document/createDocument", {
        document: {
          signatures: this.signatures,
          spectators: this.spectators,
          pdf: this.pdf,
          sortableSignatures: this.sortableSignatures,
          tries: this.tries,
          remember_every: this.remember_every,
          remember_at: this.remember_at,
          sign_mode: this.signMode,
          sign_position: this.signPosition,
          mode_logo: this.logoMode,
          company_id: this.company_id,
          signatureTypes: typesToSend,
          identityProviders: identityProviders,
          lockPhoneForOtp: this.blockPhoneVerification
        }
      }).then(
        (document) => {
          this.$vs.loading.close()
          let currentUserSignature = {
            toSign: false,
            url: ""
          }
          if (this.currentUserInSignatures) {
            currentUserSignature.toSign = true;
            let signatureCurrentUser = document.relationships.signatories.data.filter(signature => signature.attributes.email === this.user.me.email)
            currentUserSignature.url = signatureCurrentUser[0].id
          }
          this.finished = true;
          setTimeout(() => {
            router.push({name: "create-document-success", params: currentUserSignature})
          }, 100)
        })
        .catch(
          () => {
            this.$vs.loading.close()
          }
        )
    },
    validateStep1() {
      this.$store.dispatch("alert/clear", {});
      return new Promise((resolve, reject) => {
        if (!this.pdf) {
          reject("Ningún documento ha sido cargado");
          this.$store.dispatch("alert/error", "Ningún documento ha sido cargado");
        } else {
          resolve(true);
        }
      })
    },
    handleDragover (e) {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    },
    handleDrop (e) {
      e.stopPropagation()
      e.preventDefault()
      const files = e.dataTransfer.files
      if (files.length !== 1) {
        this.$vs.notify({
          title: 'Subir',
          text: 'Solo puedes subir un archivo',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning'
        })
        return
      }
      const rawFile = files[0] // only use files[0]
      this.uploadFile(rawFile)
    },
    handleClick (e) {
      const files = e.target.files
      const rawFile = files[0]
      if (!rawFile) return
      this.uploadFile(rawFile)
    },
    uploadFile (file) {
      this.$refs['fileInput'].value = null // fix can't select the same excel
      this.pdf = file;
      let read = new FileReader();
      read.readAsBinaryString(this.pdf);
      let child = this;
      child.isShowingPdf = false;
      read.onloadend = function () {
        console.log("Procesado")
        let base64 = btoa(read.result);
        let blob = child.b64toBlob(base64, 'application/pdf');
        child.base64Pdf = URL.createObjectURL(blob);
        child.isShowingPdf = true;
      }
    },
    b64toBlob(b64Data, contentType, sliceSize) {
      contentType = contentType || '';
      sliceSize = sliceSize || 512;

      var byteCharacters = atob(b64Data);
      var byteArrays = [];

      for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);

        var byteNumbers = new Array(slice.length);
        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }

      var blob = new Blob(byteArrays, {type: contentType});
      return blob;
    },
    confirmLightSignatureType() {
      if (!this.hasPhoneNumbers && this.blockPhoneVerification) {
        this.$vs.dialog({
          type: 'confirm',
          color: 'warning',
          title: 'Advertencia: Teléfonos no registrados',
          text: 'Algunos firmantes no tienen número de teléfono registrado, lo que podría causar problemas durante el proceso de verificación. ¿Desea continuar de todos modos?',
          acceptText: 'Continuar',
          cancelText: 'Cancelar',
          accept: () => {
            // Guardar la configuración y abrir el segundo popup
            this.savePhoneBlockingConfiguration();
            this.lightConfirmationPopup = false;
            setTimeout(() => {
              this.signersReviewPopup = true;
            }, 300);
          },
          cancel: () => {
            // Volver a la pantalla de configuración sin cerrar
          }
        });
      } else {
        // Guardar la configuración y abrir el segundo popup
        this.savePhoneBlockingConfiguration();
        this.lightConfirmationPopup = false;
        setTimeout(() => {
          this.signersReviewPopup = true;
        }, 300);
      }
    },
    savePhoneBlockingConfiguration() {
      this.$store.dispatch("alert/success", "Configuración de firma simple actualizada correctamente");
    },
    confirmFinalSignersReview() {
      if (this.signatureType.indexOf('VERIFICAMEX_LOCAL') === -1) {
        this.signatureType.push('VERIFICAMEX_LOCAL');
      }
      this.signersReviewPopup = false;
      this.$store.dispatch("alert/success", "Tipo de firma 'Simple' seleccionado correctamente.");
    },
    cancelLightSignatureType() {
      // Al cancelar o ir atrás, nos aseguramos de que no esté seleccionado y cerramos todo
      this.signatureType = this.signatureType.filter(type => type !== 'VERIFICAMEX_LOCAL');
      this.lightConfirmationPopup = false;
      this.signersReviewPopup = false;
    }
  },
  mounted() {
    document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.add('margin-align')
  },
  beforeRouteLeave(to, from, next) {
    if (this.finished || (!this.signPosition && !this.signMode && !this.company_id)) {
      next();
      document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.remove('margin-align')
      return;
    }
    this.$vs.dialog({
      type:'confirm',
      color: 'danger',
      title: '¿Estás seguro?',
      text: '¡Todos los cambios se perderan!',
      acceptText: "Si",
      cancelText: "No",
      accept: function () {
        next()
        document.getElementsByClassName('BeaconFabButtonFrame').item(0).classList.remove('margin-align')
      },
      cancel: function () {
        next(false)
      }
    });
  },
  watch: {
    // Observar cambios profundos en signatures
    signatures: {
      deep: true,
      handler() {
        this.phonesUpdateTrigger++;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Nuevos estilos para la sección de tipos de firma */
.signature-types-section {
  background-color: #f7f9fc;
  border-radius: 8px;
  padding: 1.5rem;
}

.signature-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.signature-types-grid .vs-checkbox--input {
  z-index: 0;
}

.signature-type-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover:not(.disabled) {
    border-color: #7c3aed;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.15);
    transform: translateY(-2px);
  }

  &.active {
    border-color: #7c3aed;
    background-color: #f3f0ff;

    .vs-checkbox {
      /deep/ .vs-checkbox--check {
        background: #7c3aed !important;
        border-color: #7c3aed !important;
      }
    }
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .card-content {
      pointer-events: none;
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .icon-wrapper {
    width: 80px;
    height: 80px;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .signature-icon {
    max-width: 100%;
    max-height: 100%;
  }

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: #1a202c;
  }

  .description {
    font-size: 0.85rem;
    color: #718096;
    margin-bottom: 1rem;
  }

  .vs-checkbox {
    margin: 0;

    /deep/ .vs-checkbox--label {
      display: none;
    }
  }
}

/* Estilos existentes */
.position-btn2{
  display: none;
  max-height: 80px!important;
}

.position-btn {
  max-height: 80px!important;
  position: relative;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -500%)!important;
}
@media (max-width: 991px) {
  .position-btn2 {
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}

.position-btn{
  max-height: 50px;
  position: relative;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -500%);
}
.vx-card .vx-card__collapsible-content .vx-card__body {
  padding: .5rem;
}
.grid-view-item {
  .grid-view-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    transition: .35s;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 4px 25px 0px rgba(0,0,0,.25);

    .grid-view-img{
      opacity: 0.9;
    }
  }
}
.opacity-50{
  opacity: .4!important;
}
.top-x{
  position: relative !important;
  top: -10px!important;
}
.menuicon-float{
  position: relative!important;
  right: 20px;
  top: 23px;
}
</style>
